/**
 * Data a person types or changes, rather than data read out of a file.
 *
 * The rest of this package decides what a column *is* from its values. Here the
 * column is already decided — somebody chose "Money" — and the job runs the other way:
 * what they typed has to become the value that column holds, or be refused where it
 * was typed. Both halves of the product use these functions, the grid in the browser
 * to outline a cell and the service to refuse a save, so the two cannot disagree about
 * whether `£1,200` is money.
 */
import type { FieldType } from "@broadpaper/core";
import { blank, booleanWord, parseDate, parseNumeric } from "./infer.js";
import { identifier } from "./names.js";
import type { SourceColumn } from "./types.js";

export type Entry = { ok: true; value: unknown } | { ok: false; reason: string };

/**
 * A stored value, as the box it is edited in shows it.
 *
 * A percentage is shown as per cent — `0.2` as `20%` — because that is what somebody
 * reads and what they will type back; `readEntry` divides it again. Dates are ISO,
 * which is what a date input takes, and never ambiguous on the way back in.
 */
export function entryText(column: SourceColumn, value: unknown): string {
  if (value === null || value === undefined) return "";
  switch (column.type) {
    case "percent":
      return typeof value === "number" ? `${round(value * 100)}%` : String(value);
    case "date":
      return typeof value === "string" ? value.slice(0, 10) : String(value);
    case "datetime":
      // What a `datetime-local` input takes. The stored value is UTC and is read back
      // as UTC, so dropping the zone here loses nothing.
      return typeof value === "string" ? value.slice(0, 16) : String(value);
    case "boolean":
      return value === true ? "Yes" : value === false ? "No" : String(value);
    default:
      return String(value);
  }
}

/**
 * What was typed, as the value the column holds — or why it cannot be.
 *
 * Generous about how a thing is written and strict about what it is: `£1,200`, `1200`
 * and `1,200.00` are all money, and `about 1200` is not. A blank is always allowed,
 * because a cell somebody has not filled in yet is not a mistake.
 */
export function readEntry(column: SourceColumn, text: string, opts: { dayFirst?: boolean } = {}): Entry {
  const s = text.trim();
  if (!s) return { ok: true, value: null };
  switch (column.type) {
    case "number":
    case "currency": {
      const n = parseNumeric(s);
      return n === null ? { ok: false, reason: `“${s}” isn't a number.` } : { ok: true, value: n };
    }
    case "percent": {
      // Per cent whether or not the sign was typed: somebody types 20 in a VAT rate
      // column and means a fifth, which is the rule `literalValue` follows too.
      const n = parseNumeric(s);
      return n === null ? { ok: false, reason: `“${s}” isn't a percentage.` } : { ok: true, value: round(n / 100) };
    }
    case "date":
    case "datetime": {
      const parsed = parseDate(s, opts.dayFirst !== false);
      if (!parsed) return { ok: false, reason: `“${s}” isn't a date BroadPaper can read — try 31/08/2026.` };
      const iso = parsed.date.toISOString();
      return { ok: true, value: column.type === "datetime" ? iso : iso.slice(0, 10) };
    }
    case "boolean": {
      const b = booleanWord(s);
      return b === null ? { ok: false, reason: `“${s}” isn't yes or no.` } : { ok: true, value: b };
    }
    default:
      return { ok: true, value: s };
  }
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})?)?$/;

/**
 * Whether a stored value is one the column's type can hold.
 *
 * The service's check on anything a browser sends. Everything downstream — the
 * projection, the totals, the date formatting — assumes a money column holds numbers
 * and a date column holds ISO dates, and a client that can send JSON can send a date
 * column full of `"next Tuesday"`.
 */
export function holds(column: SourceColumn, value: unknown): boolean {
  if (value === null) return true;
  switch (column.type) {
    case "number":
    case "currency":
    case "percent":
      return typeof value === "number" && Number.isFinite(value);
    case "date":
      return typeof value === "string" && ISO_DATE.test(value) && !Number.isNaN(Date.parse(value));
    case "datetime":
      return typeof value === "string" && ISO_DATETIME.test(value) && !Number.isNaN(Date.parse(value));
    case "boolean":
      return typeof value === "boolean";
    default:
      return typeof value === "string";
  }
}

/**
 * The columns' descriptions, brought up to date with their rows.
 *
 * Samples, blanks and whether a column is constant are what the matcher and the review
 * screen read, and after an edit they describe a file that no longer exists. Confidence
 * is one: every value was read against a type somebody chose, so there is nothing left
 * to be unsure about.
 */
export function summarise(columns: SourceColumn[], rows: Array<Record<string, unknown>>): SourceColumn[] {
  return columns.map((column) => {
    const present = rows.map((r) => r[column.name]).filter((v) => !blank(v));
    const samples: unknown[] = [];
    const seen = new Set<string>();
    for (const v of present) {
      const key = String(v);
      if (seen.has(key)) continue;
      seen.add(key);
      if (samples.length < 4) samples.push(v);
    }
    return {
      ...column,
      samples,
      confidence: 1,
      constant: present.length > 1 && seen.size === 1,
      blankRatio: rows.length ? 1 - present.length / rows.length : 1
    };
  });
}

/**
 * A column somebody added, with a name no other column has.
 *
 * The name is fixed from here on, even when the label changes: it is what a design
 * binds to, and renaming "Amt" to "Amount" must not unbind the table that shows it.
 */
export function newColumn(existing: SourceColumn[], label: string, type: FieldType = "string"): SourceColumn {
  const taken = new Set(existing.map((c) => c.name));
  const base = identifier(label) || `column${existing.length + 1}`;
  let name = base;
  for (let n = 2; taken.has(name); n++) name = `${base}${n}`;
  return retype({ header: label, name, label, type: "string", samples: [], confidence: 1, include: true, constant: false, blankRatio: 1 }, type);
}

/**
 * A column with a different type, and the settings that belong to the new one.
 *
 * A date pattern left on a column that is now money is not harmless: it travels into
 * the data source as the field's format.
 */
export function retype(column: SourceColumn, type: FieldType): SourceColumn {
  const { format, currency, ...rest } = column;
  void format;
  void currency;
  const next: SourceColumn = { ...rest, type };
  if (type === "date") next.format = column.type === "date" && column.format ? column.format : "d MMMM yyyy";
  if (type === "datetime") next.format = "d MMMM yyyy, HH:mm";
  if (type === "currency") next.currency = column.currency ?? "GBP";
  return next;
}

/** Enough decimal places for money and rates, and none of floating point's noise. */
function round(n: number): number {
  return Math.round(n * 1e9) / 1e9;
}
